import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

export const AUTH_PASSWORD_MIN_LENGTH = 8;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function resolveDefaultCountry(): CountryCode | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }

  const locale = navigator.language?.trim();
  if (!locale) {
    return undefined;
  }

  const regionMatch = locale.match(/[-_]([A-Za-z]{2})\b/);
  if (!regionMatch) {
    return undefined;
  }

  const region = regionMatch[1].toUpperCase();
  return /^[A-Z]{2}$/.test(region) ? (region as CountryCode) : undefined;
}

function normalizeEmailIdentifier(value: string): string | null {
  const normalized = value.trim();
  if (!normalized || normalized.length > 254) {
    return null;
  }

  if (!EMAIL_PATTERN.test(normalized)) {
    return null;
  }

  return normalized.toLowerCase();
}

function normalizePhoneIdentifier(value: string): string | null {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const withPlusPrefix = normalized.startsWith("00")
    ? `+${normalized.slice(2)}`
    : normalized;
  const defaultCountry = resolveDefaultCountry();
  const parsedPhone =
    parsePhoneNumberFromString(withPlusPrefix) ||
    (defaultCountry
      ? parsePhoneNumberFromString(withPlusPrefix, defaultCountry)
      : undefined);
  if (!parsedPhone || !parsedPhone.isValid()) {
    return null;
  }

  return parsedPhone.number;
}

export function normalizeAuthIdentifier(value: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "";
  }

  const normalizedEmail = normalizeEmailIdentifier(normalized);
  if (normalizedEmail) {
    return normalizedEmail;
  }

  return normalizePhoneIdentifier(normalized) ?? "";
}

export function isValidPassword(password: string): boolean {
  return password.length >= AUTH_PASSWORD_MIN_LENGTH;
}

export function isStrongPassword(password: string): boolean {
  return isValidPassword(password) && /[a-z]/i.test(password) && /\d/.test(password);
}
